import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { isNil, omitBy } from 'lodash';
import { PagedResponse, QueryPagingParams } from 'mbs-ui-shared';
import { MANUAL_ERROR_HANDLED } from 'mbs-ui-shared@interceptors/error-handler.interceptor';
import { SwitchCommercial } from 'mbs-ui-shared@models/storage-accounts/SwitchCommercial';
import { UnifiedBillingAccount } from 'mbs-ui-shared@models/storage-accounts/UnifiedBillingAccount';
import {
  AzureLifecycleRulesRequest,
  AzureVMSubscriptionsResponse,
  BucketInfo,
  Destination,
  DestinationAssignedResponse,
  DestinationsResponse,
  GetStorageAccountDestinationsPostBody,
  GetStorageAccountsPostBody,
  GoogleStorageClass,
  PartialSuccessResponse,
  S3CompatibilityCheckResponse,
  S3LifecycleRulesRequest,
  SetDestinationType,
  SetStorageType,
  StorageAccountLegacy,
  StorageAccountRegion,
  StorageAccountV2,
  StorageAccountV2ListUnit,
  StorageDestinationListUnit,
  StorageWizardCompanies,
  StoragesAvailableForCreation,
  ValidateResponse,
  ValidateStorageRequest
} from 'mbs-ui-shared@models/StorageAccount';
import { StorageType } from 'mbs-ui-shared@models/StorageType.enum';
import { CompanyType } from 'mbs-ui-shared@services/storage-accounts/storage-accounts.types';
import { StorageAccountsStore } from 'mbs-ui-shared@store/storageAccounts/storageAccounts.store';
import { BehaviorSubject, EMPTY, Observable, Subject } from 'rxjs';
import { catchError, finalize, map, pluck, tap } from 'rxjs/operators';

interface ExistStorage {
  result: boolean;
}

@Injectable({ providedIn: 'root' })
export class StorageAccountsService {
  private http = inject(HttpClient);
  private store = inject(StorageAccountsStore);

  private readonly api = {
    v1: '/api/StorageAccounts',
    v2: '/api/v2/storage-accounts',
    wizard: '/api/v2/storage-accounts/wizard',
    legacy: '/Admin/StorageAccounts'
  };

  private myLoadedFree$ = new BehaviorSubject<any>(false);
  public loadedFree$ = this.myLoadedFree$.asObservable();

  private myEntitiesFree$ = new BehaviorSubject<ExistStorage>(null);
  public entitiesFree$ = this.myEntitiesFree$.asObservable();

  private myUnifiedBillings$ = new BehaviorSubject<{
    totalCount: number;
    items: UnifiedBillingAccount[];
  }>({ totalCount: 0, items: [] });
  public unifiedBillings$ = this.myUnifiedBillings$.asObservable();

  public entities$ = toObservable(this.store.all);
  public loading$ = toObservable(this.store.loading);
  public loaded$ = toObservable(this.store.loaded);

  getAll(params = null): Observable<StorageAccountLegacy[]> {
    const result$ = new Subject<StorageAccountLegacy[]>();
    this.store.setLoading(true);
    this.http
      .get<StorageAccountLegacy[]>(this.api.v1, { params })
      .pipe(
        tap((data) => {
          this.store.setAll(data);
          result$.next(data);
          result$.complete();
        }),
        catchError(() => {
          this.store.setLoading(false);
          result$.complete();
          return EMPTY;
        }),
        finalize(() => this.store.setLoading(false))
      )
      .subscribe();
    return result$.asObservable();
  }

  getByKey(id: string): Observable<StorageAccountLegacy> {
    return this.http.get<StorageAccountLegacy>(`${this.api.v1}/${id}`);
  }

  public getS3(): Observable<StorageAccountLegacy[]> {
    return this.http.get<StorageAccountLegacy[]>('api/email-service-settings/available-s3-accounts');
  }

  public hasExistingStorage(onlyO365StorageTypes = false): Observable<ExistStorage> {
    const queryString = onlyO365StorageTypes ? `?onlyO365StorageTypes=true` : '';

    return this.http.get<ExistStorage>(`${this.api.v2}/exists${queryString}`).pipe(
      tap((existStorage) => {
        this.myEntitiesFree$.next(existStorage);
        this.myLoadedFree$.next(true);
      })
    );
  }

  public getAzureOauthUrl(): Observable<string> {
    return this.http.post<string>(`${this.api.legacy}/GetAzureOAuthURL`, {});
  }

  public switchToCommercial(storageAccountId: string): Observable<SwitchCommercial> {
    return this.http.put<SwitchCommercial>(`${this.api.v1}/${storageAccountId}/SwitchToPaidMode`, null);
  }

  public getMinimumStorageDurationForWasabi(storageAccountId: string): Observable<{ duration: number }> {
    return this.http.get<{ duration: number }>(`${this.api.v1}/${storageAccountId}/MinimumStorageDuration`);
  }

  public minimumStorageDurationForWasabi(storageAccountId: string, duration: number): Observable<any> {
    return this.http.put<any>(`${this.api.v1}/${storageAccountId}/MinimumStorageDuration`, { minimumStorageDuration: duration });
  }

  public getBuyLink(storageAccountId: string): Observable<string> {
    return this.http.get<any>(`${this.api.v1}/${storageAccountId}/SwitchToPaidMode/geturl`).pipe(pluck('url'));
  }

  public getUnifiedBillings(
    params: HttpParams | { [key: string]: string } = { visible: 'true' }
  ): Observable<{ totalCount: number; items: UnifiedBillingAccount[] }> {
    const result$ = new Subject<{ totalCount: number; items: UnifiedBillingAccount[] }>();
    this.http.get<{ totalCount: number; items: UnifiedBillingAccount[] }>(`${this.api.v1}/ub`, { params }).subscribe(
      (data) => result$.next(data),
      (err) => result$.error(err),
      () => result$.complete()
    );

    result$.subscribe((data) => this.myUnifiedBillings$.next(data));
    return result$.asObservable();
  }

  public cancelDeletionUb(storageAccountId: string): Observable<any> {
    return this.http.put<SwitchCommercial>(`${this.api.v1}/${storageAccountId}/CancelDeletion`, null);
  }

  canSwitchToPaidMode(storageId: string): Observable<boolean> {
    return this.http.get<{ allowed: boolean }>(`${this.api.v1}/ub/CanSwitchToPaidMode`).pipe(map((m) => m.allowed));
  }

  getCompanies(): Observable<CompanyType[]> {
    return this.http.get<CompanyType[]>(`${this.api.legacy}/GetProvidersCompanies`);
  }

  getStorageAccount(id: string, isDraft = false): Observable<StorageAccountV2> {
    const path = isDraft ? this.api.wizard : this.api.v2;

    return this.http.get<StorageAccountV2>(`${path}/${id}`);
  }

  getStorageAccounts(
    params: QueryPagingParams & {
      search?: string;
      orderBy?: string;
      storageType?: StorageType;
    }
  ): Observable<PagedResponse<StorageAccountV2ListUnit>> {
    return this.http.get<PagedResponse<StorageAccountV2ListUnit>>(`${this.api.v2}`, {
      params: omitBy(params, (param) => isNil(param) || param === '')
    });
  }

  getMinimumRequiredPermissions(): Observable<unknown> {
    return this.http.get<unknown>(`${this.api.wizard}/minimum-required-permissions`);
  }

  getAllDestinations(params?: { onlyO365StorageTypes?: boolean }): Observable<PagedResponse<StorageDestinationListUnit>> {
    return this.http.get<PagedResponse<StorageDestinationListUnit>>(`${this.api.v2}/destinations`, {
      params: omitBy(params || {}, isNil)
    });
  }

  getStorageAccountsPost(body: GetStorageAccountsPostBody): Observable<PagedResponse<StorageAccountV2ListUnit>> {
    return this.http.post<PagedResponse<StorageAccountV2ListUnit>>(`${this.api.v2}/list`, body);
  }

  getStorageAccountDestinations(
    id: string,
    params?: QueryPagingParams & {
      search?: string;
      orderBy?: string;
      immutabilityEnabled?: boolean;
    }
  ): Observable<DestinationsResponse> {
    return this.http.get<DestinationsResponse>(`${this.api.v2}/${id}/destinations`, {
      params: omitBy(params || {}, (param) => isNil(param) || param === '')
    });
  }

  getStorageAccountDestinationsPost(id: string, body: GetStorageAccountDestinationsPostBody): Observable<DestinationsResponse> {
    return this.http.post<DestinationsResponse>(`${this.api.v2}/${id}/destinations/list`, body);
  }

  getSDestinationAssignedPost(id: string, body: GetStorageAccountDestinationsPostBody): Observable<DestinationAssignedResponse> {
    return this.http.post<DestinationAssignedResponse>(`${this.api.v2}/${id}/destinationAssigned`, body);
  }

  getStorageAccountRegions({
    storageAccountId,
    googleStorageClass,
    fromWizard
  }: {
    storageAccountId: string;
    googleStorageClass?: string;
    fromWizard?: boolean;
  }): Observable<StorageAccountRegion[]> {
    const path = fromWizard ? this.api.wizard : this.api.v2;
    const params: {
      googleStorageClass?: string;
    } = {};

    if (googleStorageClass) {
      params.googleStorageClass = googleStorageClass;
    }

    return this.http.get<StorageAccountRegion[]>(`${path}/${storageAccountId}/regions`, {
      params
    });
  }

  getGoogleStorageClasses(fromWizard = false): Observable<GoogleStorageClass[]> {
    const path = fromWizard ? this.api.wizard : this.api.v2;

    return this.http.get<GoogleStorageClass[]>(`${path}/google/classes`);
  }

  getGoogleSubregions(region: string, googleStorageClass: string, fromWizard = false): Observable<StorageAccountRegion[]> {
    const path = fromWizard ? this.api.wizard : this.api.v2;

    return this.http.get<[]>(`${path}/google/subregions`, {
      params: {
        region,
        googleStorageClass
      }
    });
  }

  getBucketsNames({
    storageAccountId,
    fromWizard = false,
    skipError = false
  }: {
    storageAccountId: string;
    fromWizard: boolean;
    skipError: boolean;
  }): Observable<{ bucketName: string }[]> {
    const path = fromWizard ? this.api.wizard : this.api.v2;

    return this.http.get<{ bucketName: string }[]>(`${path}/${storageAccountId}/buckets`, {
      headers: skipError ? { MANUAL_ERROR_HANDLED } : {}
    });
  }

  getBucketInfo(storageAccountId: string, bucketName: string, fromWizard = false): Observable<BucketInfo> {
    const path = fromWizard ? this.api.wizard : this.api.v2;

    return this.http.get<BucketInfo>(`${path}/${storageAccountId}/buckets/${bucketName}/`, {
      headers: { MANUAL_ERROR_HANDLED }
    });
  }

  createDestination({
    storageAccountId,
    data,
    skipError
  }: {
    storageAccountId: string;
    data: SetDestinationType;
    skipError?: boolean;
  }): Observable<null | { extensions: { [key: number]: string }; instance: string; hasObjectLockWarning: boolean }> {
    return this.http.post<null>(`${this.api.v2}/${storageAccountId}/destinations`, data, {
      headers: skipError ? { MANUAL_ERROR_HANDLED } : {}
    });
  }

  editDestination({
    storageAccountId,
    data,
    destinationId,
    skipError
  }: {
    storageAccountId: string;
    data: SetDestinationType;
    skipError?: boolean;
    destinationId: string;
  }): Observable<null> {
    return this.http.put<null>(`${this.api.v2}/${storageAccountId}/destinations/${destinationId}`, data, {
      headers: skipError ? { MANUAL_ERROR_HANDLED } : {}
    });
  }

  getDestination(storageAccountId: string, destinationId: string): Observable<Destination> {
    return this.http.get<Destination>(`${this.api.v2}/${storageAccountId}/destinations/${destinationId}`);
  }

  deleteDestination(storageAccountId: string, destinationId: string) {
    return this.http.delete(`${this.api.v2}/${storageAccountId}/destinations/${destinationId}`);
  }

  setDestinationDefaultObjectLock(accountId: string, bucketName: string): Observable<boolean> {
    return this.http.put<boolean>(`${this.api.v2}/${accountId}/buckets/${bucketName}/object-lock`, {});
  }

  setDestinationS3LifecycleRules(accountId: string, destinationId: string, data: S3LifecycleRulesRequest): Observable<null> {
    return this.http.post<null>(`${this.api.v2}/${accountId}/destinations/${destinationId}/lifecycle-rule/amazon`, data, {
      headers: { MANUAL_ERROR_HANDLED }
    });
  }

  setDestinationAzureLifecycleRules(accountId: string, destinationId: string, data: AzureLifecycleRulesRequest): Observable<null> {
    return this.http.post<null>(`${this.api.v2}/${accountId}/destinations/${destinationId}/lifecycle-rule/azure`, data, {
      headers: { MANUAL_ERROR_HANDLED }
    });
  }

  setStorageAccountAzureLifecycleRules(accountId: string, data: AzureLifecycleRulesRequest): Observable<null> {
    return this.http.post<null>(`${this.api.v2}/${accountId}/lifecycle-rule/azure`, data, {
      headers: { MANUAL_ERROR_HANDLED }
    });
  }

  setStorageAccountS3LifecycleRules(accountId: string, data: S3LifecycleRulesRequest): Observable<null> {
    return this.http.post<null>(`${this.api.v2}/${accountId}/lifecycle-rule/amazon`, data, {
      headers: { MANUAL_ERROR_HANDLED }
    });
  }

  // Both endpoints work on the wizard session cache and require the credentials validate call to have run first;
  // companies are persisted to the DB only by the final wizard POST/PUT
  getWizardCompanies(accountId: string): Observable<StorageWizardCompanies> {
    return this.http.get<StorageWizardCompanies>(`${this.api.wizard}/${accountId}/companies`);
  }

  saveWizardCompanies(accountId: string, companyIds: string[]): Observable<null> {
    return this.http.put<null>(`${this.api.wizard}/${accountId}/companies`, { companyIds });
  }

  getStoragesAvailableForStorageCreation(): Observable<StoragesAvailableForCreation> {
    return this.http.get<StoragesAvailableForCreation>(`${this.api.wizard}/types`);
  }

  validateStorageWizard({ payload, id, skipError }: { payload: ValidateStorageRequest; id: string; skipError?: boolean }) {
    return this.http.put<ValidateResponse>(`${this.api.wizard}/${id}/validate`, payload, {
      headers: skipError ? { MANUAL_ERROR_HANDLED } : {}
    });
  }

  validateStorage(id: string, skipError: boolean) {
    return this.http.put<ValidateResponse>(`${this.api.v2}/${id}/validate`, null, {
      headers: skipError ? { MANUAL_ERROR_HANDLED } : {}
    });
  }

  create(id: string, data: SetStorageType): Observable<PartialSuccessResponse | null> {
    return this.http.post<PartialSuccessResponse | null>(`${this.api.wizard}/${id}`, data, {
      headers: { MANUAL_ERROR_HANDLED }
    });
  }

  edit(id: string, data: SetStorageType): Observable<PartialSuccessResponse | null> {
    return this.http.put<PartialSuccessResponse | null>(`${this.api.wizard}/${id}`, data, {
      headers: { MANUAL_ERROR_HANDLED }
    });
  }

  deleteV2(id: string, params: { deleteWithData: boolean; code?: string; redirectUri?: string }): Observable<null> {
    return this.http.delete<null>(`${this.api.v2}/${id}`, {
      params,
      headers: { MANUAL_ERROR_HANDLED }
    });
  }

  cancelDeletion(id: string): Observable<null> {
    return this.http.post<null>(`${this.api.v2}/${id}/cancel-deletion`, {});
  }

  downloadUbDetails(accountId: string, { startDate, endDate }: { startDate: string; endDate: string }): void {
    window.open(`${this.api.v2}/export/${accountId}?startDate=${startDate}&endDate=${endDate}`, '_blank');
  }

  exportStorageAccountsToCsv(fields: string[], orderBy?: string, search?: string) {
    const params = new URLSearchParams(fields.map((param) => ['fields', param]));
    orderBy && params.append('orderBy', orderBy);
    search && params.append('search', search);

    window.open(`${this.api.v2}/export?${params.toString()}`, '_blank');
  }

  getAzureVMSubscriptions(code: string, redirectUri: string): Observable<AzureVMSubscriptionsResponse> {
    return this.http.get<AzureVMSubscriptionsResponse>(`${this.api.wizard}/azureVm/subscriptions`, {
      params: {
        code,
        redirectUri
      }
    });
  }

  testEntraIdCredentials(id: string): Observable<null> {
    return this.http.get<null>(`${this.api.v2}/${id}/azure/entra-id`, {
      headers: { MANUAL_ERROR_HANDLED }
    });
  }

  provideEntraIdCredentials(id: string, credentials: { code: string; redirectUri: string }): Observable<null> {
    return this.http.post<null>(`${this.api.v2}/${id}/azure/entra-id`, credentials);
  }

  googleOauth(credentials: { code: string; redirectUri: string }): Observable<{ email: string; refreshToken: string }> {
    return this.http.get<{ email: string; refreshToken: string }>(`${this.api.wizard}/google/oauth`, {
      params: credentials
    });
  }

  createS3CompatibilityCheck(payload: SetDestinationType, accountId: string): Observable<S3CompatibilityCheckResponse> {
    return this.http.put<S3CompatibilityCheckResponse>(`${this.api.wizard}/${accountId}/compatibility-check`, payload, {
      headers: { MANUAL_ERROR_HANDLED }
    });
  }

  getS3CompatibilityCheck(taskId: string, accountId: string): Observable<S3CompatibilityCheckResponse> {
    return this.http.get<S3CompatibilityCheckResponse>(`${this.api.wizard}/${accountId}/compatibility-check/${taskId}`, {
      headers: { MANUAL_ERROR_HANDLED }
    });
  }
}
